import React from 'react';
import type { FloorPlanElement, RadiatorElement, BoilerElement, WindowElement, DoorElement, LightElement, SmokeAlarmElement, UtilityElement } from '../types';

interface ElementInfoModalProps {
  isVisible: boolean;
  element: FloorPlanElement | null;
  onClose: () => void;
  onEdit?: (element: FloorPlanElement) => void;
  isFullscreen?: boolean;
}

const ElementInfoModal: React.FC<ElementInfoModalProps> = ({
  isVisible,
  element,
  onClose,
  onEdit,
  isFullscreen = false,
}) => {
  if (!isVisible || !element) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(element);
    }
    onClose();
  };

  const renderElementInfo = () => {
    switch (element.type) {
      case 'radiator': {
        const radiator = element as RadiatorElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Radiator Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Width:</span>
                <p className="text-base text-gray-900">{radiator.width || 'Not specified'} mm</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Height:</span>
                <p className="text-base text-gray-900">{radiator.height || 'Not specified'} mm</p>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900 capitalize">{radiator.radiatorType?.replace(/-/g, ' ') || 'Not specified'}</p>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {radiator.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'boiler': {
        const boiler = element as BoilerElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Boiler Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Heating Type:</span>
                <p className="text-base text-gray-900 capitalize">{boiler.heatingType?.replace(/-/g, ' ') || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Brand:</span>
                <p className="text-base text-gray-900">{boiler.brand || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Model:</span>
                <p className="text-base text-gray-900">{boiler.model || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">GC Number:</span>
                <p className="text-base text-gray-900">{boiler.gcNumber || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {boiler.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'window': {
        const window = element as WindowElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Window Information</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Width:</span>
                <p className="text-base text-gray-900">{window.width ? `${(window.width / 1000).toFixed(2)} m` : 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Height:</span>
                <p className="text-base text-gray-900">{window.height ? `${(window.height / 1000).toFixed(2)} m` : 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Category:</span>
                <p className="text-base text-gray-900">{(window as any).windowCategory || 'Window'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Frame Material:</span>
                <p className="text-base text-gray-900">{window.windowType ? window.windowType.toUpperCase() : 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Glazing Type:</span>
                <p className="text-base text-gray-900">{(window as any).glazingType || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Glazing Gap:</span>
                <p className="text-base text-gray-900">{(window as any).glazingGap || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Trickle Vents:</span>
                <p className="text-base text-gray-900">{window.trickleVents ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Draught Proofed:</span>
                <p className="text-base text-gray-900">{window.isDraughtProofed ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Orientation:</span>
                <p className="text-base text-gray-900 flex items-center">
                  {window.orientation || 'Not calculated'}
                  {window.orientation && (
                    <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                      Auto-calculated
                    </span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {window.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'door': {
        const door = element as DoorElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Door Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Width:</span>
                <p className="text-base text-gray-900">{door.width ? `${(door.width / 1000).toFixed(2)} m` : 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900 flex items-center">
                  {(door as any).isFrontDoor ? 'Front Door' : 'Interior Door'}
                  {(door as any).isFrontDoor && (
                    <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                      Main Entrance
                    </span>
                  )}
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Undercut:</span>
                <p className="text-base text-gray-900">{door.hasUndercut ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {door.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'light': {
        const light = element as LightElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Light Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900 capitalize">{light.lightType?.replace(/-/g, ' ') || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {light.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'smoke-alarm': {
        const alarm = element as SmokeAlarmElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Smoke Alarm Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900">Smoke Alarm</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {alarm.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      case 'utility': {
        const utility = element as UtilityElement;
        return (
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-800">Utility Information</h3>
            </div>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900 capitalize">{utility.utilityType?.replace(/-/g, ' ') || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {utility.floor}</p>
              </div>
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-gray-800">Element Information</h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-600">Type:</span>
                <p className="text-base text-gray-900 capitalize">{element.type}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-600">Floor:</span>
                <p className="text-base text-gray-900">Floor {(element as any).floor || 'Unknown'}</p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {/* Invisible backdrop to close modal when clicking outside */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-50 ${isFullscreen ? 'z-[10002]' : 'z-[999]'}`}
        onClick={handleBackdropClick}
      />
      
      {/* Modal */}
      <div
        className={`fixed bg-white border border-gray-200 rounded-lg shadow-xl p-6 min-w-[300px] max-w-[400px] ${isFullscreen ? 'z-[10003]' : 'z-[1000]'}`}
        style={{
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        {renderElementInfo()}
        
        <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
          {onEdit && (
            <button
              onClick={handleEdit}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 active:bg-blue-800 transition-colors text-sm font-medium"
            >
              Edit
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 active:bg-gray-300 transition-colors text-sm font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default ElementInfoModal; 